import {
  type CodeSubmission,
  CodeSubHintResponse,
  TestCase,
  TestStatus,
  CodeSubTestResult,
  CodeSubVolume,
} from "sj-ui-toolkit/src/client";
import { getLanguageLabel } from "../languages";

import {
  ProcessedSubmission,
  SubmittedCode,
  SubmissionResult,
  SubmissionTestStatus,
  TestResult,
} from "./subTypes";

// Processes raw CodeSubmission data into ProcessedSubmission objects
export function processSubmissionData(
  submissions: CodeSubmission[],
  cases: TestCase[]
): ProcessedSubmission[] | undefined {
  if (submissions.length === 0) {
    return;
  }

  // Map each raw submission to a processed submission
  const processedSubmissions: ProcessedSubmission[] = submissions.map(submission => {
    // Make a copy of the test results to avoid mutating the original data
    const testResultsCopy = submission.testResults?.testResults
      ? [...submission.testResults.testResults]
      : [];

    // Build the base return object with runId, submittedAt, result, and testCases
    const returnObj: ProcessedSubmission = {
      runId: submission.runId,
      submittedAt: new Date(submission.submissionTime || 0),
      result: resolveSubmissionResult([...testResultsCopy]),
      tests: matchResultsToTests(cases, testResultsCopy),
    };

    // Parse and attach the submitted code (if possible)
    // The svc-code-sub nests a 'volume' array inside a 'volumes' object, and we may have
    // have 0 to N volumes: only proceed if we have exactly 1 volume
    const volumesArray = submission.volumes?.volume || [];

    const code = parseSubmittedCode(volumesArray);
    if (code) {
      returnObj.code = code;
    }

    return returnObj;
  });

  return processedSubmissions;
}

// Merges AI hints with processed submissions by matching submission IDs
export function mergeAiHintsWithSubmissions(
  submissions: ProcessedSubmission[],
  aiHints: CodeSubHintResponse[]
): ProcessedSubmission[] {
  return submissions.map(submission => {
    // Find and attach any AI hint associated with this submission
    const hintData = aiHints.find(hint => hint.submissionId === submission.runId) || null;
    if (hintData) {
      return {
        ...submission,
        aiHint: hintData.hint,
      };
    }
    return submission;
  });
}

// Resolves the overall result of a submission based on its status and test results
export function resolveSubmissionResult(resultsArray: CodeSubTestResult[]): SubmissionResult {
  // The schema nests a 'testResults' array inside a 'testResults' object, and we may
  // have 0 to N results: at present, the UI only cares whether any test failed (and if
  // so, what it was named), so let's condense that down
  let numTestsPassed = 0;
  let numTestsFailed = 0;
  let lastFailedTestName = "";
  let lastFailedTestErrorMessage = "";

  for (const result of resultsArray) {
    // testStatus is carried over from the lesson_activity schema where it must be one
    // of 'passed', 'failed', 'timeout', or 'error', but the svc-code-sub schema types it as a
    // string: if any result has any other value for testStatus, treat the result of the
    // entire submission as 'unknown'
    const status = result.testStatus;
    if (status !== "passed" && status !== "failed" && status !== "timeout" && status !== "error") {
      console.warn("Unknown test status encountered:", result);
      return { type: "unknown" };
    }

    // a timeout is a special case where we wouldn't expect more than one test result
    // to be returned, so we can immediately resolve the result to 'timeout'
    if (status === "timeout") {
      return { type: "timeout" };
    }

    // Also return 'unknown' if any test results have the 'error' status: this isn't
    // clearly defined but it generally represents an indeterminate "something went
    // wrong" state that we don't want to classify as either passed or failed
    if (status === "error") {
      return { type: "error" };
    }

    // Otherwise, count how many tests are passed vs. failed
    if (status == "passed") {
      numTestsPassed++;
    } else if (status == "failed") {
      numTestsFailed++;

      // Tests may be identified by name (AKA id) or by a failure code that identifies a functional error.
      if (result.testName) {
        lastFailedTestName = result.testName;
      }

      if (result.errorMessage) {
        lastFailedTestErrorMessage = result.errorMessage;
      }
    }
  }

  // If we had any testResults with a status of "failed", then resolve the overall
  // submission result to "failed", passing along any pertinent data
  if (numTestsFailed > 0) {
    return {
      type: "failed",
      testName: lastFailedTestName,
      errorMessage: lastFailedTestErrorMessage,
    };
  }

  // Otherwise, if all the test results we encountered had a status of "passed", then
  // consider the entire submission passed
  if (numTestsPassed > 0) {
    return { type: "passed" };
  }

  // If the submission has no test results recorded at all, its result is indeterminate
  return { type: "unknown" };
}

// Parse submitted code, decoding it for display
export function parseSubmittedCode(volumesArray: CodeSubVolume[]): SubmittedCode | null {
  // Only proceed if we have exactly 1 volume
  if (volumesArray.length !== 1) {
    return null;
  }
  const volume = volumesArray[0];

  // Volume names should generally be set, but the schema does not mandate it: abort if
  // the volume name is empty or undefined
  if (!volume.volumeName) {
    return null;
  }

  // The volume's 'code' array contains a listing of {path, fileContents} objects: only
  // proceed if we have exactly 1 user-edited file on the volume
  const files = Object.values(volume.code || {});
  if (files.length !== 1) {
    return null;
  }
  const file = files[0];

  // If fileContents is undefined (which the schema permits), don't accept it; otherwise
  // allow for the possibility of a user submitting a blank file
  if (file.fileContents === undefined) {
    return null;
  }

  // svc-code-sub returns code in base64 encoding, so we need to decode it before
  // presenting it to the UI
  const text = window.atob(file.fileContents);

  // In our single volume (presumably a repl engine like 'python' or 'dotnet'), we've
  // identified a single file (presumably the user-editable patch file, e.g.
  // 'external.py' or 'External.cs'), so we can present it to the user in the submission
  // results
  return {
    volumeName: volume.volumeName,
    languageLabel: getLanguageLabel(volume.volumeName),
    text,
  };
}

// takes the ordered test cases for this lesson and matches up the test results to the cases
export function matchResultsToTests(cases: TestCase[], results: CodeSubTestResult[]): TestResult[] {
  let failed = false;

  // check to see if there's only one result that has no name and is a timeout
  const isOnlyTimeout =
    results.length === 1 &&
    results[0].testStatus === SubmissionTestStatus.TIMEOUT &&
    results[0].testName === "";

  // errorMessage is failureCode. svc-code-sub renamed it because... ¯\_(ツ)_/¯
  const withResults = cases.map(test => {
    // If this was a timeout of the entire submission, mark all tests as skipped
    if (isOnlyTimeout) {
      return { ...test, status: SubmissionTestStatus.SKIPPED } as TestResult;
    }

    // find the result that matches this test case
    const index = results.findIndex(
      r =>
        r?.testName === ("id" in test ? test.id : undefined) ||
        r?.errorMessage === ("failureCode" in test ? test.failureCode : undefined)
    );
    const result = index >= 0 ? results.splice(index, 1)[0] : null;

    // if a previous test cases failed, that implies all following tests were skipped
    if (!result && failed) {
      return { ...test, status: SubmissionTestStatus.SKIPPED } as TestResult;
    }

    // If the current tests fails, mark as failed to true so the following cases will be marked as skipped
    if (result?.testStatus === SubmissionTestStatus.FAILED) {
      failed = true;
    }

    // if there isn't an explicit result, then the test passed (or was skipped as handled above)
    return {
      ...test,
      status: result?.testStatus || SubmissionTestStatus.PASSED,
    } as TestResult;
  });

  // if we get results that don't map to a test case, that probably indicates a problem
  // HACK: svc-code-sub returns a single result that is just {testStatus: passed} if everything passed, so ignore that if it exists
  // this is also true if the testrun is a timeout, so we can ignore that too
  const unmatched: TestResult[] = results
    .filter(
      r =>
        r.testStatus &&
        r.testStatus !== SubmissionTestStatus.PASSED &&
        r.testStatus !== SubmissionTestStatus.TIMEOUT
    )
    .map(r => buildUnmatchedTestResult(r.testName, r.errorMessage));
  // if we received a single result that was just an error, then don't show the other test cases, just show the error.
  // n.b. This is a hack due to coding challenges and mobile lessons not returning syntax compilation errors
  // if (results.length && withResults.length && unmatched.length == results.length) {
  //   withResults[0].status = SubmissionTestStatus.FAILED;
  //   withResults.slice(1).forEach(r => (r.status = SubmissionTestStatus.SKIPPED));
  //   return withResults;
  // }
  return withResults.concat(unmatched);
}

// Builds a TestResult object for unmatched test results
function buildUnmatchedTestResult(
  testName: string | undefined,
  errorMessage: string | undefined
): TestResult {
  return {
    status: SubmissionTestStatus.FAILED,
    id: testName || "",
    failureCode: errorMessage || "",
    title: "An unexpected failure occurred.",
    description: "Please try again. If the problem persists, please contact support.",
    helpUrl: "",
  };
}

export function processPollResult(
  pollSubmission: TestStatus,
  cases: TestCase[]
): ProcessedSubmission {
  let hasFailedTest = false;
  let testResults: CodeSubTestResult[] = [];

  if (pollSubmission.results) {
    testResults = pollSubmission.results
      ?.map(result => {
        // This is Typescript being dumb.  Incomplete results will never get to this state
        if (pollSubmission.status === "incomplete") {
          return null;
        }
        // We aren't getting failureCode "passed" from our BE so we are manually doing it here
        if (result.failureCode) {
          hasFailedTest = true;
        }
        return {
          errorMessage: result.failureCode,
          testName: result.id,
          testStatus: hasFailedTest
            ? pollSubmission.status
            : ("passed" as "passed" | "failed" | "error"), // once again -- Our BE types being wrong
          testOutput: result.output,
        };
      })
      .filter(result => result !== null); // Remove null entries -- again, will never happen
  }
  // Build submissionResult
  let overallResult: SubmissionResult;
  if (pollSubmission.status === "incomplete") {
    overallResult = {
      type: "incomplete",
      progress: pollSubmission.progress,
    };
  } else if (pollSubmission.status === "timeout") {
    overallResult = { type: "timeout" };
  } else if (pollSubmission.status === "error") {
    overallResult = { type: "error" };
  } else {
    overallResult = resolveSubmissionResult(testResults);
  }

  // Build the base return object with runId, submittedAt, result, and testCases
  const returnObj: ProcessedSubmission = {
    runId: pollSubmission.runId,
    submittedAt: new Date(pollSubmission.createdAt || 0),
    result: overallResult,
    tests: matchResultsToTests(cases, testResults),
  };

  // convert pollSubmission volume to CodeSubmission volume
  const volumes =
    pollSubmission.code?.volumes?.map(v => ({
      code: v.files?.map(f => ({ path: f.path, fileContents: f.contents })) || [],
      volumeName: v.name,
    })) ?? [];

  // Add code if possible
  const code = parseSubmittedCode(volumes);
  if (code) {
    returnObj.code = code;
  }

  // No AI hints can be available for a submission that was just made/polled, so return
  return returnObj;
}
